import type { To } from 'react-router';
import { merge } from 'lodash-es';
import React, { useId } from 'react';
import { useLocation } from 'react-router';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { VariantProp } from '../../common/colors';
import type { BadgeProps } from '../../components/Badge/Badge';
import type { TooltipProps } from '../../components/Tooltip/Tooltip';
import type { ControlSize } from '../../controls/shared/types';
import type { WidthTypes } from '../../utilities/shared/Width';
import type { WrapProps } from '../../utilities/shared/Wrap';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { Badge } from '../../components/Badge/Badge';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { Link } from '../../components/Link/Link';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { selectors, transitions } from '../../controls/shared/styles';
import {
  controlBorderRadius,
  controlPaddingX,
  controlPaddingY,
  controlVars,
  getControlBadgeSize,
  getControlIconSize,
  getControlPreset,
} from '../../controls/shared/vars';
import { darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { displayCSS } from '../../utilities/shared/Display';
import { type MaxWidthTypes, maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { type MinWidthTypes, minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { wrapCSS } from '../../utilities/shared/Wrap';

const TargetIcon = styled(DynamicIcon, {
  color: variantIconColor,
});

const TargetLabel = styled(Text, {
  color: variantBodyColor,
});

const TargetContents = styled(AlignStack, {
  position: 'relative',
  zIndex: 2,
});

const TargetBoundary = styled('div', {
  position: 'absolute',
  zIndex: 1,
  top: `calc(${controlPaddingY} * -1)`,
  right: `calc(${controlPaddingX} * -1)`,
  bottom: `calc(${controlPaddingY} * -1)`,
  left: `calc(${controlPaddingX} * -1)`,
  background: variantBackgroundColor,
  strokeAll: variantStrokeColor,
  borderRadius: controlBorderRadius,
  opacity: '$$boundaryOpacity',
  transition: transitions.control,
});

const TargetContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  maxWidth: '100%',
  verticalAlign: 'bottom',
  transition: transitions.control,
  $$boundaryOpacity: 0,
  ...getVariantStyles('neutral'),

  variants: {
    hasAnnotation: {
      true: {},
      false: {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',

        [selectors.hover]: {
          $$boundaryOpacity: 0.8,
        },

        [selectors.focus]: {
          outline: 'none',
          $$boundaryOpacity: 0.8,

          [`${TargetBoundary}`]: {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {
        cursor: 'default',
        ...getVariantStyles('neutral'),
      },
    },
    selected: {
      true: {
        ...getVariantStyles('brand'),
      },
      false: {},
    },
    size: {
      'x-small': {
        ...controlVars.size['x-small'],
      },
      small: {
        ...controlVars.size.small,
      },
      medium: {
        ...controlVars.size.medium,
      },
      large: {
        ...controlVars.size.large,
      },
      'x-large': {
        ...controlVars.size['x-large'],
      },
    },
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});

export type TargetPropSize = 'large' | 'medium' | 'small';

export type TargetBaseProps = {
  annotation?: BadgeProps | BadgeProps[];
  'aria-label'?: string;
  children?: React.ReactNode;
  display?: 'block' | 'flex' | 'inline-block' | 'inline-flex';
  icon?: DynamicIconName;
  label?: React.ReactNode;
  onClick?: (event: any) => void;
  size?: ControlSize;
  selected?: boolean;
  tooltip?: TooltipProps;
  variant?: VariantProp;
  wrap?: 'nowrap' | 'wrap';
  width?: {
    width?: WidthTypes;
    maxWidth?: MaxWidthTypes;
    minWidth?: MinWidthTypes;
  };
};

export type TargetProps<Tag extends React.ElementType = 'span'> = PolymorphicComponentProps<
  Tag,
  TargetBaseProps
>;

export function Target<Tag extends React.ElementType>({
  annotation,
  as = 'span' as Tag,
  children,
  display = 'flex',
  icon,
  label,
  onClick,
  selected,
  size = 'medium',
  tooltip,
  variant,
  wrap = 'wrap',
  width,
  ref,
  ...remaining
}: TargetProps<Tag>) {
  const effectivePreset = getControlPreset(size);
  const effectiveIconSize = getControlIconSize(size);
  const effectiveAnnotationSize = getControlBadgeSize(size);
  const isPressable = Boolean(onClick || (as !== 'span' && as !== 'div'));
  const effectiveVariant = isPressable ? variant ?? 'alternative' : variant;
  const css = merge(
    displayCSS(display),
    widthCSS(width?.width),
    maxWidthCSS(width?.maxWidth),
    minWidthCSS(width?.minWidth),
  );

  const renderAnnotation = Array.isArray(annotation) ? (
    <BadgeGroup size={effectiveAnnotationSize}>
      {annotation.map((item) => (
        <Badge key={item.icon} {...item} />
      ))}
    </BadgeGroup>
  ) : (
    <Badge {...annotation} size={effectiveAnnotationSize} />
  );

  const render = (
    <TargetContainer
      ref={ref}
      as={as}
      onClick={onClick}
      role={onClick && 'button'}
      tabIndex={isPressable ? 0 : undefined}
      hasAnnotation={!!annotation}
      isPressable={isPressable}
      size={size}
      selected={selected}
      variant={effectiveVariant}
      css={css}
      {...remaining}
    >
      <TargetContents
        direction="row"
        gap={size === 'large' ? 6 : 4}
        preset={effectivePreset}
        start={icon && <TargetIcon icon={icon} size={effectiveIconSize} />}
        end={annotation && renderAnnotation}
      >
        {(children != null || label != null) && (
          <TargetLabel
            truncate={wrap === 'nowrap'}
            weight="bold"
            whitespace={wrap === 'wrap' ? 'normal' : 'no-wrap'}
          >
            {children ?? label}
          </TargetLabel>
        )}
      </TargetContents>
      <TargetBoundary />
    </TargetContainer>
  );

  return tooltip ? <Tooltip {...tooltip}>{render}</Tooltip> : render;
}

const TargetsToken = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'no-wrap',
  alignItems: 'center',
});

const TargetsContainer = styled('div', {
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  verticalAlign: 'bottom',
  gap: '$6',
});

function TargetsOverflow({
  targets,
  maximumTargets,
  overflow,
}: {
  targets: TargetProps<any>[];
  maximumTargets?: number;
  overflow?: {
    as?: React.ElementType;
    to?: To;
    onClick?: () => void;
  };
}) {
  const overflowCount = maximumTargets ? targets.length - maximumTargets : undefined;
  const location = useLocation();
  const isCurrentPath = overflow?.to && location.pathname === overflow.to;

  let moreControl: React.ReactNode;
  if (overflow?.onClick) {
    moreControl = (
      <Link as="button" type="button" variant="alternative" onClick={overflow.onClick}>
        {overflowCount} more
      </Link>
    );
  } else if (overflow && !isCurrentPath) {
    moreControl = (
      <Link as={overflow?.as} to={overflow?.to}>
        {overflowCount} more
      </Link>
    );
  } else {
    moreControl = <Text>{overflowCount} more</Text>;
  }

  return <Text>and {moreControl}</Text>;
}

export function Targets<Tag extends React.ElementType>({
  display = 'flex',
  hideSeparator = false,
  maximumTargets,
  overflow,
  size = 'medium',
  targets,
  variant,
  wrap = 'wrap',
}: {
  display?: 'block' | 'flex' | 'inline-block' | 'inline-flex';
  hideSeparator?: boolean;
  maximumTargets?: number;
  size?: TargetPropSize;
  variant?: VariantProp;
  targets: TargetProps<Tag>[];
  overflow?: {
    as?: React.ElementType;
    to?: To;
    onClick?: () => void;
  };
} & WrapProps) {
  const id = useId();
  const css = merge(displayCSS(display), wrapCSS(wrap));

  return (
    <TargetsContainer css={css}>
      {targets.slice(0, maximumTargets ?? targets.length).map((target, index) => (
        <TargetsToken key={`${id}-${target['aria-label'] ?? target.label ?? index}`}>
          <Target display={display} size={size} variant={variant} {...target} />
          {index < targets.length - 1 && !hideSeparator && <Text>,</Text>}
        </TargetsToken>
      ))}
      {maximumTargets && targets.length > maximumTargets && (
        <TargetsOverflow targets={targets} maximumTargets={maximumTargets} overflow={overflow} />
      )}
    </TargetsContainer>
  );
}
